import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off, remove } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import ProductForm from './AddProduct';

const baseButton = {
  padding: '0.875rem 2rem',
  borderRadius: '8px',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer',
  border: 'none',
  fontSize: '0.95rem',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
  transform: 'translateY(0)',
};

const ProductTable = () => {
  const [products, setProducts] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
        setProducts([]);
        setLoading(false);
        setShowForm(false);
        setEditingProduct(null);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    const encodedEmail = encodeEmailForPath(currentUser.email);
    const productsRef = ref(database, `users/${encodedEmail}/products`);

    const handleData = (snapshot) => {
      const productsData = [];
      snapshot.forEach((childSnapshot) => {
        productsData.push({
          id: childSnapshot.key,
          ...childSnapshot.val(),
        });
      });
      setProducts(productsData);
      setLoading(false);
    };

    const handleError = (error) => {
      console.error('Error fetching products:', error);
      setLoading(false);
    };

    onValue(productsRef, handleData, handleError);

    return () => {
      off(productsRef, 'value', handleData);
    };
  }, [currentUser]);

  const toggleForm = () => {
    setShowForm(!showForm);
    setEditingProduct(null);
  };

  const handleEdit = (product) => {
    setEditingProduct({ ...product });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!currentUser || !productId) return;

    try {
      const encodedEmail = encodeEmailForPath(currentUser.email);
      const productRef = ref(database, `users/${encodedEmail}/products/${productId}`);
      if (window.confirm('Are you sure you want to delete this product?')) {
        await remove(productRef);
        setProducts(products.filter((product) => product.id !== productId));
        alert('Product deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Error deleting product');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingProduct(null);
    setLoading(true);
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingProduct(null);
  };

  const exportToCSV = () => {
    const headers = ['P.ID', 'Product Name', 'HSN', 'Group', 'Brand', 'Purchase Tax', 'Sales Tax', 'MRP', 'Cost Price', 'Selling Price', 'Margin (%)'];
    const rows = filteredProducts.map((product, index) => [
      index + 1,
      product.productName || 'N/A',
      product.hsnSacCode || 'N/A',
      product.group || 'N/A',
      product.brand || 'N/A',
      `${product.purchaseTax || '0'}%`,
      `${product.salesTax || '0'}%`,
      `₹${product.mrp || '0.00'}`,
      `₹${product.purchaseRateExclTax || '0.00'}`,
      `₹${product.sellingRate || '0.00'}`,
      `${product.margin || '0.00'}%`,
    ]);

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'products_data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const filteredProducts = products.filter(
    (product) =>
      (product.productName?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (product.hsnSacCode?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (product.group?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
      (product.brand?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
  );

  const styles = {
    container: {
      maxWidth: '100%',
      margin: '0 auto',
      padding: '2rem',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: 'white',
      borderRadius: '6px',
      border: '1px solid #d1d5db',
      fontWeight: '600',
      color: '#374151',
    },
    title: {
      fontSize: '2rem',
      fontWeight: '700',
      color: '#2563eb',
      margin: '0',
    },
    addButton: {
      ...baseButton,
      backgroundColor: '#2563eb',
    },
    exportButton: {
      ...baseButton,
      backgroundColor: '#4caf50',
      marginRight: '1rem',
    },
    searchStyle: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: '1px solid #d1d5db',
      fontSize: '0.95rem',
      width: '200px',
    },
    tableContainer: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e2e8f0',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.9rem',
      border: '1px solid #d1d5db',
    },
    tableHeader: {
      padding: '1rem 0.75rem',
      textAlign: 'center',
      backgroundColor: '#2563eb',
      color: 'white',
      fontWeight: '600',
      fontSize: '0.85rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      border: '1px solid #1e40af',
    },
    tableCell: {
      padding: '1rem 0.75rem',
      border: '1px solid #d1d5db',
      color: '#374151',
      verticalAlign: 'middle',
      textAlign: 'center',
    },
    noData: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#718096',
      fontSize: '1.1rem',
    },
    srNoCell: {
      fontWeight: '600',
      textAlign: 'center',
      width: '60px',
    },
    actionCell: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0.5rem',
    },
    editButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#10b981',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
      transform: 'translateY(0)',
    },
    deleteButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      backgroundColor: '#ef4444',
      color: 'white',
      fontWeight: '500',
      cursor: 'pointer',
      border: 'none',
      fontSize: '0.8rem',
      transition: 'all 0.2s ease',
      boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
      transform: 'translateY(0)',
    },
    productNameCell: {
      fontWeight: '600',
      color: '#111827',
      textAlign: 'center',
    },
    priceCell: {
      fontWeight: '600',
      color: '#1f2937',
      textAlign: 'center',
    },
    marginCell: {
      fontWeight: '600',
      color: '#10b981',
      textAlign: 'center',
    },
    taxCell: {
      color: '#6b21a8',
      fontWeight: '500',
      textAlign: 'center',
    },
  };

  if (!currentUser) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '4rem', color: '#718096', fontSize: '1.1rem' }}>
          <h2>Please login to access the Product Management</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Product Management</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        
          <input
            type="text"
            placeholder="Search Product Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchStyle}
          />
          <button
            onClick={toggleForm}
            style={styles.addButton}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
            }}
          >
            {showForm ? '← Back to Products' : '+ Add New Product'}
          </button>
        </div>
      </div>
      {showForm && (
        <div style={{ marginBottom: '2rem' }}>
          <ProductForm
            key={editingProduct ? editingProduct.id : 'new'}
            editingProduct={editingProduct}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
          />
        </div>
      )}
      {!showForm && (
        <div style={styles.tableContainer}>
          {loading ? (
            <div style={styles.noData}>
              <p>⏳ Loading products...</p>
            </div>
          ) : filteredProducts.length > 0 ? (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.tableHeader, ...styles.srNoCell }}>P.ID</th>
                  <th style={styles.tableHeader}>Product Name</th>
                  <th style={styles.tableHeader}>HSN</th>
                  <th style={styles.tableHeader}>Group</th>
                  <th style={styles.tableHeader}>Brand</th>
                  <th style={styles.tableHeader}>Purchase Tax</th>
                  <th style={styles.tableHeader}>Sales Tax</th>
                  <th style={styles.tableHeader}>MRP</th>
                  <th style={styles.tableHeader}>Cost Price</th>
                  <th style={styles.tableHeader}>Selling Price</th>
                  <th style={styles.tableHeader}>Margin (%)</th>
                  <th style={styles.tableHeader}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product, index) => (
                  <tr
                    key={product.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#f7fafc' : 'white',
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <td style={{ ...styles.tableCell, ...styles.srNoCell }}>{index + 1}</td>
                    <td style={{ ...styles.tableCell, ...styles.productNameCell }}>{product.productName || '-'}</td>
                    <td style={styles.tableCell}>{product.hsnSacCode || '-'}</td>
                    <td style={styles.tableCell}>{product.group || '-'}</td>
                    <td style={styles.tableCell}>{product.brand || '-'}</td>
                    <td style={{ ...styles.tableCell, ...styles.taxCell }}>{product.purchaseTax || '0'}%</td>
                    <td style={{ ...styles.tableCell, ...styles.taxCell }}>{product.salesTax || '0'}%</td>
                    <td style={{ ...styles.tableCell, ...styles.priceCell }}>₹{product.mrp || '0.00'}</td>
                    <td style={{ ...styles.tableCell, ...styles.priceCell }}>₹{product.purchaseRateExclTax || '0.00'}</td>
                    <td style={{ ...styles.tableCell, ...styles.priceCell }}>₹{product.sellingRate || '0.00'}</td>
                    <td style={{ ...styles.tableCell, ...styles.marginCell }}>
                      {product.margin ? `${parseFloat(product.margin).toFixed(2)}%` : '0.00%'}
                    </td>
                    <td style={{ ...styles.tableCell, ...styles.actionCell }}>
                      <button
                        onClick={() => handleEdit(product)}
                        style={styles.editButton}
                        title="Edit Product"
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        style={styles.deleteButton}
                        title="Delete Product"
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.3)';
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={styles.noData}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: '0.5' }}>📦</div>
              <h3 style={{ color: '#4a5568', marginBottom: '0.5rem' }}>No Products Found</h3>
              <p style={{ marginBottom: '1.5rem' }}>Get started by adding your first product!</p>
              <button
                onClick={toggleForm}
                style={{
                  ...styles.addButton,
                  fontSize: '1rem',
                  padding: '1rem 2rem',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
                }}
              >
                + Add Your First Product
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductTable;